import React, { useState, useEffect, useContext } from 'react';
import CustomModal from "../../Elements/CustomModal";
import { UserContext } from "../../../context/UserContext";
import { getImageCacheKey, getImageUrl, invalidateImageCache } from '../../../lib/helpers';
import { isValidEmail } from '../../../lib/email';

function SuperuserModal({ active, handleModal, id, setErrorMessage, onSuccess }) {
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [token] = useContext(UserContext);
  const [image, setImage] = useState(null);
  const [currentImageUrl, setCurrentImageUrl] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [localError, setLocalError] = useState('');

  // Al abrir el modal en modo "crear" (id = null), siempre limpiar el formulario.
  // Si no, al crear un superusuario y abrir de nuevo, React no re-dispara el effect
  // anterior porque `id` ya era null.
  useEffect(() => {
    if (!active) return;
    if (id) return;
    setEmail('');
    setFirstName('');
    setLastName('');
    setPassword('');
    setConfirmPassword('');
    setImage(null);
    setCurrentImageUrl(null);
    setLocalError('');
    if (setErrorMessage) setErrorMessage('');
  }, [active, id, setErrorMessage]);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImage(file);
      const url = URL.createObjectURL(file);
      setCurrentImageUrl(url);
    }
  };

  useEffect(() => {
    async function getUser() {
      if (!id || !token) return;
      const requestOptions = {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: "Bearer " + token,
        },
      };

      try {
        const response = await fetch(`/v1/users/${id}`, requestOptions);
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({ detail: 'Could not get user data' }));
          const errorMsg = errorData.detail || 'Could not get user data';
          setLocalError(errorMsg);
          if (setErrorMessage) setErrorMessage(errorMsg);
          return;
        }
        const data = await response.json();
        if (data.email) {
          setEmail(data.email);
        }
        if (data.profile?.first_name) {
          setFirstName(data.profile.first_name);
        }
        if (data.profile?.last_name) {
          setLastName(data.profile.last_name);
        }
        // Cargar imagen actual si existe
        if (data.profile?.image_path) {
          setCurrentImageUrl(getImageUrl(data.profile.image_path, {
            cacheKey: getImageCacheKey('superuser', data.id || id),
          }));
        }
      } catch (error) {
        console.error('Error fetching user:', error);
        const errorMsg = 'Could not get user data';
        setLocalError(errorMsg);
        if (setErrorMessage) setErrorMessage(errorMsg);
      }
    }
    if (id) {
      getUser();
    } else {
      // Reset form when creating new user
      setEmail('');
      setFirstName('');
      setLastName('');
      setPassword('');
      setConfirmPassword('');
      setImage(null);
      setCurrentImageUrl(null);
      setLocalError('');
    }
  }, [id, token, setErrorMessage]);

  const submitRegistration = async () => {
    setIsSubmitting(true);
    setLocalError('');
    if (setErrorMessage) setErrorMessage('');

    // Validaciones
    if (!isValidEmail(email)) {
      const errorMsg = 'Por favor, ingresa un email válido';
      setLocalError(errorMsg);
      if (setErrorMessage) setErrorMessage(errorMsg);
      setIsSubmitting(false);
      return;
    }

    // Validar contraseña solo si se proporcionó (para creación es opcional si el usuario ya existe)
    if (password) {
      if (password.length < 8) {
        const errorMsg = 'La contraseña debe tener al menos 8 caracteres';
        setLocalError(errorMsg);
        if (setErrorMessage) setErrorMessage(errorMsg);
        setIsSubmitting(false);
        return;
      }
      if (password !== confirmPassword) {
        const errorMsg = 'Las contraseñas no coinciden';
        setLocalError(errorMsg);
        if (setErrorMessage) setErrorMessage(errorMsg);
        setIsSubmitting(false);
        return;
      }
    }

    const payload = {
      email: email.trim(),
      ...(password && { password: password }), // Solo incluir si se proporciona
      profile: {
        ...(firstName.trim() && { first_name: firstName.trim() }),
        ...(lastName.trim() && { last_name: lastName.trim() }),
      }
    };

    const formData = new FormData();
    formData.append('user_data', JSON.stringify(payload));
    if (image) {
      formData.append('image', image);
    }

    const requestOptions = {
      method: id ? 'PUT' : 'POST',
      headers: { 
        Authorization: "Bearer " + token
      },
      body: formData,
    };
    
    try {
      const endpoint = id ? `/v1/users/superuser/${id}` : `/v1/users/superuser`;
      const response = await fetch(endpoint, requestOptions);
      
      if (!response.ok) {
        const errorText = await response.text();
        let errorData;
        try {
          errorData = JSON.parse(errorText);
        } catch {
          errorData = { detail: errorText || `Error ${response.status}: ${response.statusText}` };
        }
        const errorMsg = errorData.detail || `Error ${response.status}: ${response.statusText}`;
        setLocalError(errorMsg);
        if (setErrorMessage) setErrorMessage(errorMsg);
        setIsSubmitting(false);
        return;
      }
      
      const data = await response.json();
      console.log(`✅ Superuser ${id ? 'actualizado' : 'creado'} exitosamente:`, data);
      invalidateImageCache(getImageCacheKey('superuser', data.id || id));
      if (onSuccess) {
        onSuccess(data);
      }
      handleModal();
    } catch (error) {
      console.error('❌ Error:', error);
      const errorMsg = error.detail || error.message || 'An error occurred.';
      setLocalError(errorMsg);
      if (setErrorMessage) setErrorMessage(errorMsg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    submitRegistration();
  };

  const footer = (
    <>
      <button type="button" className="wa-btn-secondary" onClick={handleModal} disabled={isSubmitting}>
        Cancelar
      </button>
      <button
        type="submit"
        form="superuser-modal-form"
        className="wa-btn-primary disabled:opacity-60 disabled:cursor-not-allowed"
        disabled={isSubmitting}
      >
        {isSubmitting ? 'Guardando...' : (id ? 'Actualizar' : 'Crear')}
      </button>
    </>
  );

  return (
    <CustomModal
      active={active}
      handleModal={handleModal}
      title={id ? 'Editar Superusuario' : 'Crear Superusuario'}
      size="large"
      titleId="superuser-modal-title"
      footer={footer}
    >
      <form id="superuser-modal-form" onSubmit={handleSubmit} className="wa-form space-y-4">
        {localError && (
          <div className="wa-form-error" role="alert">
            {localError}
          </div>
        )}

        <div className="flex flex-col lg:flex-row gap-6 lg:items-start">
          <div className="flex flex-col items-center lg:items-start shrink-0">
            {currentImageUrl ? (
              <img
                src={currentImageUrl}
                alt="Vista previa"
                className="w-32 h-32 rounded-full object-cover border-2 border-[var(--wa-border)]"
              />
            ) : (
              <div className="w-32 h-32 rounded-full bg-[var(--wa-muted)] flex items-center justify-center text-[var(--wa-foreground)] font-bold text-3xl border border-[var(--wa-border)]">
                {email.charAt(0).toUpperCase() || 'U'}
              </div>
            )}
            <label className="mt-3 cursor-pointer">
              <span className="wa-btn-secondary inline-block text-center text-sm py-2 px-4 h-auto">
                {currentImageUrl ? 'Cambiar imagen' : 'Subir imagen'}
              </span>
              <input
                type="file"
                accept="image/*"
                onChange={handleImageChange}
                className="hidden"
                disabled={isSubmitting}
              />
            </label>
            <p className="mt-2 text-xs text-[var(--wa-muted-foreground)]">
              La imagen de perfil es opcional.
            </p>
          </div>

          <div className="flex-1 min-w-0 space-y-3">
            <div className="wa-form-field wa-form-field-full">
              <label htmlFor="superuser-email" className="wa-label">
                Email <span className="text-red-600">*</span>
              </label>
              <input
                type="email"
                id="superuser-email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="wa-input"
                required
                disabled={isSubmitting}
                autoComplete="email"
              />
            </div>

            <div className="wa-form-grid-cols-2">
              <div className="wa-form-field">
                <label htmlFor="superuser-firstName" className="wa-label">Nombre</label>
                <input
                  type="text"
                  id="superuser-firstName"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  className="wa-input"
                  disabled={isSubmitting}
                  autoComplete="given-name"
                />
              </div>
              <div className="wa-form-field">
                <label htmlFor="superuser-lastName" className="wa-label">Apellidos</label>
                <input
                  type="text"
                  id="superuser-lastName"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  className="wa-input"
                  disabled={isSubmitting}
                  autoComplete="family-name"
                />
              </div>
            </div>

            <div className="wa-form-grid-cols-2">
              <div className="wa-form-field">
                <label htmlFor="superuser-password" className="wa-label">
                  Contraseña <span className="text-[var(--wa-muted-foreground)] font-normal text-xs">(opcional si el usuario ya existe)</span>
                </label>
                <input
                  type="password"
                  id="superuser-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="wa-input"
                  minLength={8}
                  disabled={isSubmitting}
                  autoComplete="new-password"
                />
                <p className="text-xs text-[var(--wa-muted-foreground)] mt-1">Mínimo 8 caracteres</p>
              </div>
              {password && (
                <div className="wa-form-field">
                  <label htmlFor="superuser-confirmPassword" className="wa-label">
                    Confirmar contraseña <span className="text-red-600">*</span>
                  </label>
                  <input
                    type="password"
                    id="superuser-confirmPassword"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="wa-input"
                    minLength={8}
                    disabled={isSubmitting}
                    autoComplete="new-password"
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </form>
    </CustomModal>
  );
}

export default SuperuserModal;
